import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { RootState } from ".";
import { IAuth, ILogin } from "@interfaces";
import { authApi, clinicApi } from "@api";
import { enumAdminRole } from "@configs";
import { updateProgram, updateTemp } from "./app";

interface IState {
  isAuth: boolean;
  auth: IAuth | null;
  loading: boolean;
}

const initialState: IState = {
  isAuth: false,
  auth: null,
  loading: false,
};
export const login = createAsyncThunk<IAuth, ILogin>(
  "auth/login",
  async (values: ILogin, { rejectWithValue }) => {
    try {
      const res = await authApi.login(values);
      return res.data as IAuth;
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);
export const clinicLogin = createAsyncThunk<IAuth, ILogin>(
  "auth/clinicLogin",
  async (values: ILogin, { dispatch, rejectWithValue }) => {
    try {
      const res = await authApi.clinicLogin(values);
      const authData = res.data as IAuth;

      const isClinicUser = authData?.userRole === enumAdminRole.CLINIC;

      if (isClinicUser && authData?.userId && authData?.token) {
        dispatch(updateTemp(authData.token));
        const clinicRes = await clinicApi.clinic(String(authData.userId));
        dispatch(updateProgram(clinicRes?.data?.joinedProgram || []));
      }

      return authData;
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);
const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    resetAuth: () => initialState,
    logout: (state) => {
      state.auth = null;
    },
    setRefresh: (state, action: PayloadAction<string>) => {
      state.auth = { ...state.auth, token: action.payload };
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.auth = { ...state.auth, token: action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.fulfilled, (state, action: PayloadAction<IAuth>) => {
      state.loading = false;
      state.auth = action.payload;
    });

    builder.addCase(login.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(login.rejected, (state) => {
      state.auth = null;
      state.loading = false;
    });
    builder.addCase(clinicLogin.fulfilled, (state, action: PayloadAction<IAuth>) => {
      state.loading = false;
      state.auth = action.payload;
    });

    builder.addCase(clinicLogin.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(clinicLogin.rejected, (state) => {
      state.auth = null;
      state.loading = false;
    });
  },
});

export const { resetAuth, logout, setRefresh } = authSlice.actions;

export const selectAuth = (state: RootState) => state.auth;

export default authSlice.reducer;
